import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Grid, Stack } from '@mui/material'
import { format } from 'date-fns'

import { DateRange } from '@mui/x-date-pickers-pro'
import { FieldTouch, StyledGrid } from '../../OccurrenceRequest'
import {
  EnhancementDay,
  EnhancementGetResponse,
  StatusResponse,
  SubmitStatus,
} from '../../../models/enhancement'
import DaysCheckboxList from '../DaysCheckboxList/DaysCheckboxList'
import Card from '../../../shared/layout/Card'
import { hasDaysChanged } from '../hasDaysChanged'
import Modal from '../../../shared/UI/Modal'
import { RootStore, useAppDispatch } from '../../../redux/store'
import {
  hideModal,
  SetShowModalPayload,
  showModalDialog,
} from '../../../redux/reducers/appSettingsReducer'
import DateTimePicker from '../../../shared/UI/DateTimePicker'
import { enhancementContent, enhancementTypes, globalContent } from '../../../utils/constants'
import { isCodedOnCall } from '../../../utils/codedOnCall'
import { ValidationErrors } from '../../CallOutEnhancement/validations'
import {
  formatDateWithTimeZoneStr,
  getDurationInFractionalHours,
  getLocalDateString,
} from '../../../utils/date-utils'
import Alert from '../../../shared/UI/Alert/Alert'

import { DateFormats } from '../../../api/absence/tempTypes/generalprops'
import { v2MyActionsService } from '../../../services/myActionsServiceV2'
import { showErrorMessage, showSuccessMessage } from '../../../redux/reducers/snackbarReducer'
import {
  isReadOnlyForEmployeeEnhancement,
  isReadOnlyForManagerEnhancement,
} from '../../../utils/SharedMethods/isReadOnlyEnhancement'
import { calculateCalloutDays } from '../../../utils/SharedMethods/calculateCallOutDays'
import { latestApproverComment } from '../../../utils/SharedMethods/getCommentsFromStatusHistory'
import {
  calculateTotalHours,
  calculateTotalShifts,
  requestDayHasFieldError,
} from '../../MyRequests/utils'
import ActionedInfo from '../../../shared/UI/ActionedInfo/ActionedInfo'
import { RequestDetailResponse } from '../../../types/requests'
import CardTitleWithLink from '../../../shared/UI/CardTitleWithLink/CardTitleWithLink'
import AddComment from '../../../shared/UI/AddComment/AddComment'
import ReviewerComments from '../../../shared/UI/AddComment/ReviewerComments'
import ActionButtons from '../../../shared/UI/ActionButtons/ActionButtons'
import ApprovalDrawer from './ApprovalDrawer'

export default function Approval({
  days,
  id,
  dateRange,
  enhancementType,
  statusHistory,
  currentStatus,
  setTotalHours,
  onStatusChangeSuccess,
  onCardTitleClick,
  requestDetail,
  drawerOpen,
  toggleDrawer,
  employeeLocationId,
  employeeLocationGroupId,
}: EnhancementGetResponse & {
  onStatusChangeSuccess: () => void
  setTotalHours: (hours: string) => void
  onCardTitleClick: (open: boolean) => void
  requestDetail: RequestDetailResponse | null
  setRequestDetail: React.Dispatch<React.SetStateAction<RequestDetailResponse | null>>
  hasChanged: boolean
  isDeclinedOrCancelled: boolean
  commentError?: boolean
  drawerOpen: boolean
  toggleDrawer: () => void
  employeeLocationId?: number
  employeeLocationGroupId?: number
}) {
  const dispatch = useAppDispatch()
  const [requestedDays, setRequestedDays] = useState<EnhancementDay[]>(
    JSON.parse(JSON.stringify(days!))
  )
  const [comments, setComments] = useState<string>(latestApproverComment(statusHistory))
  const [commentsError, setCommentsError] = useState<boolean>(false)
  const [requestedDateRange, setRequestedDateRange] = useState<DateRange<Date>>([
    new Date(dateRange.start),
    new Date(dateRange.end),
  ])
  const [declineLoading, setDeclineLoading] = useState<boolean>(false)
  const [formErrors, setFormErrors] = useState<ValidationErrors>()
  const { showModal, title, message, type, buttonLabel } = useSelector<
    RootStore,
    SetShowModalPayload
  >((state: RootStore) => state.appSettings.modalProps)
  const isCalloutEnhancment = enhancementType === enhancementTypes.callOut
  const useShifts = isCodedOnCall(enhancementType, days)
  const isReadOnlyRequest = isReadOnlyForManagerEnhancement(currentStatus!)
  const [fieldsTouched, setFieldsTouched] = useState<FieldTouch>({
    reviewerComments: false,
    rows: false,
  })

  const actionedHistoryStatus = useMemo(
    () => statusHistory.filter(item => item.submitStatus !== SubmitStatus.PENDING),
    [statusHistory]
  )
  const [approveWithChangesLoading, setApproveWithChangesLoading] = useState<boolean>(false)
  const GetMostRecentHistoryStatus = useCallback(
    (a: StatusResponse, b: StatusResponse) => (new Date(a.date) > new Date(b.date) ? a : b),
    []
  )
  const mostRecentAction: StatusResponse | undefined = useMemo(
    () =>
      actionedHistoryStatus.length
        ? actionedHistoryStatus.reduce(GetMostRecentHistoryStatus)
        : undefined,
    [GetMostRecentHistoryStatus, actionedHistoryStatus]
  )
  const [showReviewerComments, setShowReviewerComments] = useState<boolean>(
    !!latestApproverComment(statusHistory)
  )
  const resetForm = () => {
    setRequestedDateRange([new Date(dateRange.start), new Date(dateRange.end)])
    setRequestedDays(JSON.parse(JSON.stringify(days!)))
  }

  useEffect(() => resetForm(), [dateRange, days])

  useEffect(() => {
    /**
     * Check if the call out duration is valid
     */
    const validateCalloutDuration = () => {
      const [start, end] = requestedDateRange
      if (start && end) {
        // TODO [SWKBSSPRPS-797]: Validate that end time is greater than start time
        setFormErrors(prevState => ({
          ...prevState,
          start: false,
          end: false,
        }))
      }
    }
    if (isCalloutEnhancment && requestedDateRange[0] && requestedDateRange[1]) {
      validateCalloutDuration()
      const allDays = calculateCalloutDays(requestedDateRange)
      setRequestedDays(
        allDays.map(day => ({
          day: formatDateWithTimeZoneStr(day.day),
          hours: day.hours,
          checked: day.checked,
        }))
      )
    }
  }, [requestedDateRange])

  const handleDaysChange = (updatedDays: EnhancementDay[]) => {
    const ehnacementDays = [...updatedDays]
    if (useShifts) {
      const shifts = calculateTotalShifts(ehnacementDays)
      setFormErrors(prevState => ({
        ...prevState,
        duration: shifts <= 0,
      }))
      setTotalHours(shifts.toString())
      setRequestedDays(ehnacementDays)
      return
    }
    const hours = calculateTotalHours(ehnacementDays)
    setFormErrors(prevState => ({
      ...prevState,
      duration: hours <= 0,
    }))
    setTotalHours(hours.toFixed(2))
    setRequestedDays(ehnacementDays)
  }

  const handleCallOutTimePickerChange = (toDate: Date | null, fromDate: Date | null) => {
    if (!toDate || !fromDate) return
    setTotalHours(getDurationInFractionalHours(toDate, fromDate).toFixed(2))
    setRequestedDateRange([toDate, fromDate])
  }

  const hasDateRangeChanged =
    requestedDateRange[0]?.getTime() !== new Date(dateRange.start).getTime() ||
    requestedDateRange[1]?.getTime() !== new Date(dateRange.end).getTime()

  const validateDateRange = () => {
    if (!requestedDateRange[0] || !requestedDateRange[1]) {
      setFormErrors(prevState => ({
        ...prevState,
        start: !requestedDateRange[0],
        end: !requestedDateRange[1],
      }))
      return false
    }
    return true
  }

  const hasChanges = () => {
    if (isCalloutEnhancment) return hasDateRangeChanged
    return hasDaysChanged(days!, requestedDays)
  }
  const buildApprovalBody = (submitStatus: SubmitStatus) => {
    const body = {
      id,
      days,
      dateRange,
      status: {
        submitStatus,
        comments,
      },
    }
    if (submitStatus === SubmitStatus.DECLINED) {
      resetForm()
      return body
    }
    return {
      ...body,
      days: requestedDays,
      dateRange: {
        start: format(requestedDateRange[0]!, DateFormats.DATE_AND_TIME),
        end: format(requestedDateRange[1]!, DateFormats.DATE_AND_TIME),
      },
    }
  }
  const putRequest = (submitStatus: SubmitStatus, messageText: string) => {
    validateDateRange()
    v2MyActionsService
      .putEnhancementManual(buildApprovalBody(submitStatus))
      .then(() => {
        setApproveWithChangesLoading(false)
        dispatch(showSuccessMessage(`Request successfully ${messageText}`))
        onStatusChangeSuccess()
        if (drawerOpen) {
          toggleDrawer()
        }
      })
      .catch(() => {
        dispatch(showErrorMessage(`Failed request has not been ${messageText}`))
      })
      .finally(() => {
        dispatch(hideModal())
        setDeclineLoading(false)
      })
  }

  const declineRequest = () => {
    setDeclineLoading(true)
    putRequest(SubmitStatus.DECLINED, 'declined')
  }

  const canApprove = () => !(hasChanges() && requestDetail?.reviewerComments === '')

  const approveRequest = () => {
    if (hasChanges() && !comments) {
      setCommentsError(!comments)
      setShowReviewerComments(true)
      return
    }

    if (canApprove()) {
      setApproveWithChangesLoading(true)

      if (hasChanges()) {
        putRequest(SubmitStatus.AMENDED, 'amended')
      } else {
        putRequest(SubmitStatus.APPROVED, 'approved')
      }
    }
  }

  const onApproveWithChanges = () => {
    setApproveWithChangesLoading(true)
    if (hasChanges() && !comments) {
      setFieldsTouched({ ...fieldsTouched, reviewerComments: true })
      setCommentsError(!comments)
      setShowReviewerComments(true)
      setApproveWithChangesLoading(false)
    } else {
      approveRequest()
    }
  }

  const displayClashModal = () => {
    if (!comments) {
      setCommentsError(!comments)
      setShowReviewerComments(true)
      return
    }
    if (!commentsError) {
      dispatch(
        showModalDialog({
          title: 'Are you sure?',
          message: `Are you sure you would like to decline this request?`,
          buttonLabel: 'Decline',
          type: 'question',
          showModal: true,
        })
      )
    }
  }

  const declineDisabled =
    currentStatus === SubmitStatus.DECLINED || currentStatus === SubmitStatus.SUBMITTED
  const handleCommentChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setComments(e.target.value)
    if (e.target.value) {
      setCommentsError(false)
    }
  }

  const hasInvalidDays = () => requestedDays.some(day => requestDayHasFieldError(day, null))
  const handleAddCommentClick = () => {
    setShowReviewerComments(true)
  }

  const isButtonDisabled = (): boolean =>
    (isReadOnlyForEmployeeEnhancement(currentStatus!) && !hasChanges()) ||
    !!formErrors?.duration ||
    hasInvalidDays()
  return (
    <Card>
      <CardTitleWithLink onClick={onCardTitleClick} />
      <Grid container spacing={4}>
        {isCalloutEnhancment ? (
          <>
            <StyledGrid
              item
              xs={12}
              sx={{
                overflowY: 'scroll',
                maxHeight: '250px',
                marginTop: '20px',
              }}
            >
              <Grid item xs={12}>
                <DateTimePicker
                  id="approval-date-from"
                  value={requestedDateRange[0]}
                  maxDate={new Date()}
                  onChange={date => handleCallOutTimePickerChange(date, requestedDateRange[1])}
                  label={globalContent.from}
                  minutesStep={5}
                  error={formErrors?.start}
                  helperText={globalContent.required}
                  dataTestId="callout-from-date"
                  disabled={isReadOnlyRequest}
                />
              </Grid>
              <Grid item xs={12} mt={2}>
                <DateTimePicker
                  id="approval-date-to"
                  value={requestedDateRange[1]}
                  maxDate={new Date()}
                  onChange={date => handleCallOutTimePickerChange(requestedDateRange[0], date)}
                  label={globalContent.to}
                  minutesStep={5}
                  error={formErrors?.end}
                  helperText={globalContent.required}
                  dataTestId="callout-to-date"
                  disabled={isReadOnlyRequest}
                />
              </Grid>
            </StyledGrid>
          </>
        ) : (
          <>
            <StyledGrid
              item
              xs={12}
              sx={{
                overflowY: 'auto',
                maxHeight: showReviewerComments ? 'calc(100vh - 650px)' : 'calc(100vh - 525px)',
                marginTop: '20px',
              }}
            >
              <Grid>
                <DaysCheckboxList
                  days={requestedDays}
                  disableCheckbox={isReadOnlyRequest}
                  disableTextField={isReadOnlyRequest}
                  onDaysChange={updatedDays => {
                    handleDaysChange(updatedDays)
                  }}
                  error={day => requestDayHasFieldError(day, null)}
                  employeeLocationId={employeeLocationId}
                  employeeLocationGroupId={employeeLocationGroupId}
                />
              </Grid>
            </StyledGrid>
            <Grid item xs={12}>
              {formErrors?.duration ? (
                <Alert severity="error" message={enhancementContent.zeroHoursValidation} />
              ) : null}
            </Grid>
          </>
        )}

        <Grid item xs={12} style={{ paddingTop: '0', marginTop: '-20px', marginBottom: '16px' }}>
          <AddComment onClick={handleAddCommentClick} />
          <Stack direction="row">
            {showReviewerComments && (
              <ReviewerComments
                label="Reviewer comments"
                reviewerComments={comments}
                onBlur={() => {
                  setFieldsTouched({ ...fieldsTouched, reviewerComments: true })
                }}
                onChange={e => handleCommentChange(e)}
                error={commentsError}
                disabled={isReadOnlyRequest}
                dataTestId="reviewer-comments"
              />
            )}
          </Stack>
          <br />
        </Grid>
        <Grid
          item
          xs={12}
          direction={{ xs: 'column', md: 'row' }}
          sx={{
            paddingRight: '6px',
            '&.MuiGrid-item': {
              paddingTop: '0px',
              marginTop: '-40px',
            },
          }}
        >
          <ActionButtons
            declineDisabled={declineDisabled}
            submitDisabled={isButtonDisabled()}
            approveLoading={approveWithChangesLoading}
            onDecline={displayClashModal}
            onApprove={approveRequest}
            hasChanges={hasChanges()}
            showRejectButton={false}
            onApproveWithChanges={onApproveWithChanges}
            declineLabel="Decline"
          />
        </Grid>
      </Grid>
      <Modal
        type={type}
        open={showModal}
        onClose={() => {
          dispatch(hideModal())
        }}
        onClick={declineRequest}
        title={title}
        message={message}
        buttonLabel={buttonLabel}
        buttonLoading={declineLoading}
      />
      <ApprovalDrawer
        open={drawerOpen}
        onClose={toggleDrawer}
        requestedDays={requestedDays}
        handleDaysChange={handleDaysChange}
        comments={comments}
        setComments={setComments}
        commentsError={commentsError}
        isReadOnlyRequest={isReadOnlyRequest}
        declineDisabled={declineDisabled}
        isButtonDisabled={isButtonDisabled}
        approveWithChangesLoading={approveWithChangesLoading}
        displayClashModal={displayClashModal}
        approveRequest={approveRequest}
        onApproveWithChanges={onApproveWithChanges}
        showReviewerComments={showReviewerComments}
        setShowReviewerComments={setShowReviewerComments}
        fieldsTouched={fieldsTouched}
        setFieldsTouched={setFieldsTouched}
        hasChanges={hasChanges}
        employeeLocationId={employeeLocationId}
        employeeLocationGroupId={employeeLocationGroupId}
      />
    </Card>
  )
}
